import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

export const UNGROUPED_KEY = "__sin_grupo__";

// Carpetas de la lista de Automatizaciones con sus tarjetas. Arrastrar una tarjeta a otra carpeta la cambia de grupo
// (onMove); el orden dentro de la carpeta es por nombre, así que soltar en la misma carpeta no hace nada. Una carpeta
// nueva aparece vacía como zona para soltar hasta que recibe su primera automatización.
const AutomationGroups = ({ groups, onMove, onDragStart, onDragEnd, canDrag, renderCard, itemKey }) => {
    const handleDragEnd = (result) => {
        onDragEnd?.();
        const { destination, source, draggableId } = result;
        if (!destination || destination.droppableId === source.droppableId) return;

        const item = groups.flatMap((group) => group.items).find((candidate) => itemKey(candidate) === draggableId);
        if (!item) return;
        onMove(item, destination.droppableId === UNGROUPED_KEY ? null : destination.droppableId);
    };

    return (
        <DragDropContext onDragStart={onDragStart} onDragEnd={handleDragEnd}>
            <Stack spacing={2.5}>
                {groups.map((group) => (
                    <Stack key={`automation-group-${group.key}`} spacing={1.25}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    color: "primary.main",
                                    bgcolor: "rgba(103, 58, 183, 0.08)",
                                    flex: "0 0 auto",
                                }}>
                                <FolderOutlinedIcon fontSize="small" />
                            </Box>
                            <Box>
                                {/* m: 0 — el tema le da margen inferior a los h6 y el título quedaba descentrado del icono. */}
                                <Typography variant="h6" sx={{ lineHeight: 1.15, m: 0 }}>{group.name}</Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {group.items.length || group.total
                                        ? `${group.total ?? group.items.length} ${(group.total ?? group.items.length) === 1 ? "automatización" : "automatizaciones"}`
                                        : "Carpeta nueva"}
                                </Typography>
                            </Box>
                        </Stack>

                        <Droppable droppableId={group.key}>
                            {(provided, snapshot) => (
                                <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{
                                        borderRadius: 3,
                                        minHeight: group.items.length ? undefined : 64,
                                        outline: snapshot.isDraggingOver ? "2px dashed" : "none",
                                        outlineColor: "primary.light",
                                        outlineOffset: 4,
                                        bgcolor: snapshot.isDraggingOver ? "rgba(103, 58, 183, 0.04)" : "transparent",
                                        border: group.items.length ? "none" : "1px dashed",
                                        borderColor: "divider",
                                        // Centrar solo el aviso de la carpeta vacía: en una carpeta con tarjetas, place-items también
                                        // encoge cada tarjeta (block layout) y quedan de 30 px.
                                        ...(group.items.length ? {} : { display: "grid", placeItems: "center" }),
                                        transition: "background-color 150ms ease",
                                    }}>
                                    {!group.items.length && (
                                        <Typography variant="body2" color="text.secondary">Arrastra aquí una automatización</Typography>
                                    )}
                                    {group.items.map((item, index) => (
                                        <Draggable key={itemKey(item)} draggableId={itemKey(item)} index={index} isDragDisabled={!canDrag(item)}>
                                            {(dragProvided, dragSnapshot) => (
                                                <Box
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                    sx={{ mb: 1.1, opacity: dragSnapshot.isDragging ? 0.92 : 1 }}>
                                                    {renderCard(item, dragSnapshot.isDragging)}
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </Stack>
                ))}
            </Stack>
        </DragDropContext>
    );
};

export default AutomationGroups;
